/**
 * WORLD-CUP-COUNTRY-DOMINATION-P0 — Country Experience Engine.
 *
 * Single source of truth for per-country nationalization:
 * accent colors, hero copy, share copy, broadcast partners,
 * Telegram channels, WhatsApp templates and local CTAs.
 *
 * No backend coupling. No mocks. Pure config + typed accessors.
 * Backends (Auth, Stripe, Billing, RLS, Prediction, Nation Wars,
 * API-Football, Sentry, PostHog) remain untouched.
 */

export type CountryCode = "BR" | "MX" | "AR" | "IN";

export interface BroadcastPartner {
  id: string;
  name: string;
  /** Public site — VerseSport never redistributes signal. */
  url: string;
  note?: string;
}

export interface TelegramChannelRef {
  label: string;
  url: string;
}

export interface WhatsAppTemplate {
  intent: "referral" | "nation" | "prediction" | "influencer";
  message: string;
}

export interface CountryInfluencerRef {
  slug: string;
  displayName: string;
  handle: string;
  emoji: string;
  placeholder?: boolean;
}

export interface CountryAccent {
  primary: string;
  secondary: string;
  tertiary: string;
}

export interface CountryExperienceConfig {
  country_code: CountryCode;
  country_name: string;
  language: string;
  flag: string;
  accent_colors: CountryAccent;
  hero_copy: { title: string; subtitle: string };
  share_copy: string;
  national_teams: string[];
  influencers: CountryInfluencerRef[];
  broadcast_partners: BroadcastPartner[];
  telegram_channels: TelegramChannelRef[];
  whatsapp_templates: WhatsAppTemplate[];
  local_ctas: { id: string; label: string }[];
  disclaimer: string;
}

export const COUNTRY_EXPERIENCE_REGISTRY: Record<CountryCode, CountryExperienceConfig> = {
  BR: {
    country_code: "BR",
    country_name: "Brasil",
    language: "pt-BR",
    flag: "🇧🇷",
    accent_colors: {
      primary: "oklch(0.55 0.18 145)",   // verde
      secondary: "oklch(0.85 0.18 95)",  // amarelo
      tertiary: "oklch(0.45 0.18 255)",  // azul
    },
    hero_copy: {
      title: "🇧🇷 VerseSport Brasil",
      subtitle: "A segunda tela oficial da torcida brasileira",
    },
    share_copy: "Vem para o VerseSport Brasil — a torcida no Mundial",
    national_teams: ["Seleção Brasileira"],
    influencers: [
      { slug: "casimiro", displayName: "Casimiro", handle: "@casimiro", emoji: "🎙️" },
      { slug: "futebol", displayName: "Futebol Brasil", handle: "@futebolbr", emoji: "⚽" },
      { slug: "torcidasp", displayName: "Torcida SP", handle: "@torcidasp", emoji: "🟡" },
    ],
    broadcast_partners: [
      { id: "cazetv", name: "CazéTV", url: "https://www.youtube.com/@CazeTV" },
      { id: "globo", name: "TV Globo", url: "https://globoplay.globo.com/agora-na-tv/" },
      { id: "sportv", name: "SporTV", url: "https://sportv.globo.com/" },
      { id: "nsports", name: "N Sports", url: "https://nsports.com.br/" },
      { id: "globoplay", name: "Globoplay", url: "https://globoplay.globo.com/" },
    ],
    telegram_channels: [
      { label: "VerseSport Brasil", url: "https://t.me/versesport_brasil" },
    ],
    whatsapp_templates: [
      { intent: "referral", message: "Vem para o VerseSport com a torcida brasileira no Mundial!" },
      { intent: "nation", message: "Bora montar a torcida da Seleção no VerseSport!" },
      { intent: "prediction", message: "Acertei minha previsão no VerseSport — bora competir?" },
    ],
    local_ctas: [
      { id: "predict", label: "Fazer previsão agora" },
      { id: "ranking", label: "Ver ranking da Seleção" },
    ],
    disclaimer: "VerseSport não está afiliado à CazéTV, Globo, SporTV, N Sports nem Globoplay.",
  },
  MX: {
    country_code: "MX",
    country_name: "México",
    language: "es-MX",
    flag: "🇲🇽",
    accent_colors: {
      primary: "oklch(0.55 0.18 145)",   // verde
      secondary: "oklch(0.65 0.22 25)",  // rojo
      tertiary: "oklch(0.97 0.02 95)",   // blanco
    },
    hero_copy: {
      title: "🇲🇽 VerseSport México",
      subtitle: "La segunda pantalla de la afición mexicana",
    },
    share_copy: "Únete a VerseSport México — la afición del Tri en el Mundial",
    national_teams: ["Selección Mexicana"],
    influencers: [
      { slug: "creador-1", displayName: "Creador MX 1", handle: "@creadormx1", emoji: "🟢", placeholder: true },
      { slug: "creador-2", displayName: "Creador MX 2", handle: "@creadormx2", emoji: "🔴", placeholder: true },
      { slug: "creador-3", displayName: "Creador MX 3", handle: "@creadormx3", emoji: "⚪", placeholder: true },
    ],
    broadcast_partners: [
      { id: "tudn", name: "TUDN", url: "https://www.tudn.com/" },
      { id: "azteca", name: "TV Azteca", url: "https://www.tvazteca.com/aztecadeportes/" },
      { id: "televisa", name: "Televisa Deportes", url: "https://www.televisa.com/deportes/" },
    ],
    telegram_channels: [
      { label: "VerseSport México", url: "https://t.me/versesport_mexico" },
    ],
    whatsapp_templates: [
      { intent: "referral", message: "Vente a VerseSport México con la afición del Tri en el Mundial 🇲🇽" },
      { intent: "nation", message: "Arma la porra del Tri en VerseSport ⚽" },
      { intent: "prediction", message: "Acerté mi pronóstico en VerseSport, ¿le entras?" },
    ],
    local_ctas: [
      { id: "predict", label: "Pronostica el partido del Tri" },
      { id: "ranking", label: "Ver ranking de la Selección" },
    ],
    disclaimer: "VerseSport no está afiliado a TUDN, TV Azteca ni Televisa.",
  },
  AR: {
    country_code: "AR",
    country_name: "Argentina",
    language: "es-AR",
    flag: "🇦🇷",
    accent_colors: {
      primary: "oklch(0.78 0.10 220)",   // celeste
      secondary: "oklch(0.97 0.02 95)",  // blanco
      tertiary: "oklch(0.78 0.10 220)",  // celeste
    },
    hero_copy: {
      title: "🇦🇷 VerseSport Argentina",
      subtitle: "La segunda pantalla de la hinchada Albiceleste",
    },
    share_copy: "Sumate a VerseSport Argentina — la hinchada Albiceleste en el Mundial",
    national_teams: ["Selección Albiceleste"],
    influencers: [
      { slug: "creador-1", displayName: "Creador AR 1", handle: "@creadorar1", emoji: "🔵", placeholder: true },
      { slug: "creador-2", displayName: "Creador AR 2", handle: "@creadorar2", emoji: "⚪", placeholder: true },
      { slug: "creador-3", displayName: "Creador AR 3", handle: "@creadorar3", emoji: "🇦🇷", placeholder: true },
    ],
    broadcast_partners: [
      { id: "tycsports", name: "TyC Sports", url: "https://www.tycsports.com/" },
      { id: "tvpublica", name: "TV Pública", url: "https://www.tvpublica.com.ar/" },
      { id: "telefe", name: "Telefe", url: "https://mitelefe.com/" },
    ],
    telegram_channels: [
      { label: "VerseSport Argentina", url: "https://t.me/versesport_argentina" },
    ],
    whatsapp_templates: [
      { intent: "referral", message: "Vení a VerseSport Argentina con la hinchada Albiceleste 🇦🇷" },
      { intent: "nation", message: "Armá la hinchada de la Selección en VerseSport" },
      { intent: "prediction", message: "Le pegué al pronóstico en VerseSport, ¿te animás?" },
    ],
    local_ctas: [
      { id: "predict", label: "Pronosticá el partido de la Selección" },
      { id: "ranking", label: "Ver ranking de la Albiceleste" },
    ],
    disclaimer: "VerseSport no está afiliado a TyC Sports, TV Pública ni Telefe.",
  },
  IN: {
    country_code: "IN",
    country_name: "India",
    language: "en-IN",
    flag: "🇮🇳",
    accent_colors: {
      primary: "oklch(0.72 0.16 60)",    // saffron
      secondary: "oklch(0.97 0.02 95)",  // white
      tertiary: "oklch(0.55 0.18 145)",  // green
    },
    hero_copy: {
      title: "🇮🇳 VerseSport India",
      subtitle: "Join the world's largest football fan movement.",
    },
    share_copy: "Join VerseSport India — the World Cup fan community",
    national_teams: ["India National Team"],
    influencers: [
      { slug: "creador-1", displayName: "Creator IN 1", handle: "@creatorin1", emoji: "🟠", placeholder: true },
      { slug: "creador-2", displayName: "Creator IN 2", handle: "@creatorin2", emoji: "⚪", placeholder: true },
      { slug: "creador-3", displayName: "Creator IN 3", handle: "@creatorin3", emoji: "🟢", placeholder: true },
    ],
    broadcast_partners: [
      { id: "sonyliv", name: "Sony LIV", url: "https://www.sonyliv.com/" },
      { id: "jiocinema", name: "JioCinema", url: "https://www.jiocinema.com/" },
      { id: "starsports", name: "Star Sports", url: "https://www.starsports.com/" },
    ],
    telegram_channels: [
      { label: "VerseSport India", url: "https://t.me/versesport_india" },
    ],
    whatsapp_templates: [
      { intent: "referral", message: "Join me on VerseSport India for the World Cup 🇮🇳" },
      { intent: "nation", message: "Build your World Cup squad on VerseSport" },
      { intent: "prediction", message: "I just nailed a prediction on VerseSport — your turn!" },
    ],
    local_ctas: [
      { id: "predict", label: "Make your prediction" },
      { id: "ranking", label: "See the global ranking" },
    ],
    disclaimer: "VerseSport is not affiliated with Sony LIV, JioCinema or Star Sports.",
  },
};

export const SUPPORTED_COUNTRIES: CountryCode[] = ["BR", "MX", "AR", "IN"];

export function normalizeCountry(code: string | null | undefined): CountryCode | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  return (SUPPORTED_COUNTRIES as string[]).includes(upper) ? (upper as CountryCode) : null;
}

export function getCountryExperience(country: string | null | undefined): CountryExperienceConfig | null {
  const c = normalizeCountry(country);
  return c ? COUNTRY_EXPERIENCE_REGISTRY[c] : null;
}

export function getCountryLanguage(country: string | null | undefined): string | null {
  return getCountryExperience(country)?.language ?? null;
}

export function getCountryAccent(country: string | null | undefined): CountryAccent | null {
  return getCountryExperience(country)?.accent_colors ?? null;
}

export function getCountryInfluencers(country: string | null | undefined): CountryInfluencerRef[] {
  return getCountryExperience(country)?.influencers ?? [];
}

export function getBroadcastPartner(country: string, id: string): BroadcastPartner | null {
  return getCountryExperience(country)?.broadcast_partners.find((b) => b.id === id) ?? null;
}

/** Stable canonical path per country (used by CountrySwitcher + SEO). */
export const COUNTRY_HOME_PATH: Record<CountryCode, string> = {
  BR: "/brasil",
  MX: "/mexico",
  AR: "/argentina",
  IN: "/india",
};